/**
 * Redux async actions (src/store/actions/teacherSessionActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../helpers/axiosconfig"; // Update path

const normalizeSessionStatusPayload = (payload = {}) => {
  const data = payload?.data || {};
  return {
    ...payload,
    isActive: Boolean(data.isActive),
    sessionDetails: data.sessionDetails || null,
  };
};

/**
 * Async thunk: teacherJoinSession.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const teacherJoinSession = createAsyncThunk(
  "teacherSession/joinSession",
  async ({ bookingId, teacherId, launchOnly = false }, { rejectWithValue }) => {
    try {
      const joinUrl = `teacher-join-session/${bookingId}/${teacherId}`;
      const response = launchOnly === true
        ? await axiosInstance.post(joinUrl, { launchOnly: true })
        : await axiosInstance.post(joinUrl);
      return response.data;
    } catch (error) {
      const responseData = error.response?.data;
      return rejectWithValue(
        responseData && typeof responseData === 'object'
          ? { ...responseData, httpStatus: error.response?.status || null }
          : {
              message: responseData || error.message || 'Failed to register teacher join session',
              httpStatus: error.response?.status || null,
            }
      );
    }
  }
);

/**
 * Async thunk: getTeacherSessionStatus.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getTeacherSessionStatus = createAsyncThunk(
  'teacherSession/getStatus',
  async ({ bookingId, teacherId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`teacher-session-status/${bookingId}/${teacherId}`);
      return normalizeSessionStatusPayload(response.data);
    } catch (error) {
      console.error('Teacher session status error:', error);
      return rejectWithValue(error.response?.data || 'Failed to get teacher status');
    }
  }
);

/**
 * Async thunk: studentJoinSession.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const studentJoinSession = createAsyncThunk(
  "studentSession/joinSession",
  async ({ bookingId, studentId, launchOnly = false }, { rejectWithValue }) => {
    try {
      const joinUrl = `student-join-session/${bookingId}/${studentId}`;
      const response = launchOnly === true
        ? await axiosInstance.post(joinUrl, { launchOnly: true })
        : await axiosInstance.post(joinUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message || 'Failed to register student join session' });
    }
  }
);

/**
 * Async thunk: getStudentSessionStatus.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getStudentSessionStatus = createAsyncThunk(
  "studentSession/getStatus", 
  async ({ bookingId, studentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`student-session-status/${bookingId}/${studentId}`);
      return normalizeSessionStatusPayload(response.data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
